/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

import { SUPPORTED_OS } from './modelMapper/appModelMapper/appModelConstants';
import FilterUtil from './filterUtil';

/**
 * Method to randomize array
 * @param  {object} array
 * Usage: randomizes array in-place
 */

/* eslint-disable no-param-reassign */
export function shuffleArray(array) {
    let m = array.length;
    let t;
    let i;
    while (m) {
        i = Math.floor(Math.random() * m);
        m -= 1;
        t = array[m];
        array[m] = array[i];
        array[i] = t;
    }
}
/* eslint-disable no-param-reassign */

export const ANALYTICS_CONSTANTS = {
    EN_STR_NOT_AVAILABLE: 'english-string-not-available',
};

export const getOSDisplayString = (supportedOS) => {
    if (supportedOS === SUPPORTED_OS.APPLE) return 'iOS';
    if (supportedOS === SUPPORTED_OS.ANDROID) return 'Android';
    if (supportedOS === SUPPORTED_OS.WINDOWS) return 'Windows';
    return supportedOS;
};

export function getContentExt(mediaList = []) {
    let contentExt;
    if (mediaList.length === 1) {
        contentExt = mediaList[0].image ? 'image' : 'video';
    } else if (mediaList.length > 1) {
        contentExt = 'combo';
    }
    return contentExt;
}

function filteredAppsForSingleSection(apps, section, sectionType) {
    const { filter, compositeFilter } = section;
    let filteredApps = [];
    if (compositeFilter) {
        filteredApps = FilterUtil.getFilteredApps(apps, section,
            sectionType, compositeFilter);
        filteredApps = filteredApps.filter(app => !app.isHidden);
    } else if (filter) {
        const filterIds = filter.split('|');
        filterIds.forEach((filterId) => {
            if (filterId) {
                const appsForFilterId = apps.filter(app => (
                    app[sectionType].includes(filterId)
                    && !app.isHidden));
                filteredApps.push(...appsForFilterId);
            }
        });
    } else {
        filteredApps = apps.filter(app => (
            app[sectionType].includes(section.id)
            && !app.isHidden));
    }
    return filteredApps;
}

// filter apps to be shown in a section
export function filterAppsForAppSection(allAppsModel, appSection, subSection, sectionHeader) {
    // Pick apps to be shown in this section from contenful
    // skeleton data
    const { includedSapCodeList } = sectionHeader;
    let filteredTiles = allAppsModel.apps.slice();
    // if (compositeFilter) {
    //     filteredTiles = FilterUtil.getFilteredApps(allAppsModel, {
    //         appSectionFilter: appSection.id,
    //         subSectionFilter: subSection.id,
    //         isHidden: false,
    //     }, compositeFilter);
    // } else if (filter) {
    //     const filterIds = filter.split('|');
    //     filterIds.forEach((filterId) => {
    //         if (filterId) {
    //             const filteredApps = allAppsModel.filterApps({
    //                 appSectionFilter: appSection.id,
    //                 subSectionFilter: subSection.id,
    //                 subHeadingFilter: filterId,
    //                 isHidden: false,
    //             });
    //             filteredTiles.push(...filteredApps);
    //         }
    //     });
    // } else {
    //     filteredTiles = allAppsModel.filterApps({
    //         appSectionFilter: appSection.id,
    //         subSectionFilter: subSection.id,
    //         subHeadingFilter: sectionHeader.id,
    //         isHidden: false,
    //     });
    // }
    filteredTiles = filteredAppsForSingleSection(filteredTiles, appSection, 'appSectionFilter');
    if (subSection && subSection.id) {
        filteredTiles = filteredAppsForSingleSection(filteredTiles, subSection, 'subSectionFilter');
    }
    if (sectionHeader && sectionHeader.id) {
        filteredTiles = filteredAppsForSingleSection(filteredTiles, sectionHeader, 'subHeadingFilter');
    }

    let includedApps;
    // if includedSapCodeList is sent, use its order
    // else use ffc sort order
    if (includedSapCodeList && includedSapCodeList.length) {
        includedApps = includedSapCodeList
            .reduce((tiles, sapCode) => {
                if (sapCode) {
                    const latestVersionProduct = allAppsModel.getLatestVersionProdforSapcode(
                        sapCode, { appSectionFilter: appSection.id }
                    );
                    if (latestVersionProduct) {
                        tiles.push(latestVersionProduct);
                    }
                }
                return tiles;
            }, []);

        if (!filteredTiles || !filteredTiles.length) {
            filteredTiles = includedApps;
        } else {
            const intersection = includedApps.filter((includedApp) => {
                const { sapcode } = includedApp;
                const correspondingAppTiles = filteredTiles.filter(
                    tile => tile.sapcode === sapcode
                );
                return correspondingAppTiles.length > 0;
            });
            filteredTiles = intersection;
        }
    } else {
        filteredTiles.sort((a, b) => {
            if (a.sortOrder
              && b.sortOrder
              && typeof a.sortOrder === 'object'
              && typeof b.sortOrder === 'object') {
                return a.sortOrder[appSection.id] - b.sortOrder[appSection.id];
            }
            return 0;
        });
    }

    return filteredTiles;
}
